import React from "react";
import axios from "axios";
import { feature as topojsonParse } from "topojson-client";
import useCitiesData from "../../hooks/use-cities-data";
import styles from "./map.module.scss";
import { useIsMobile } from "../../hooks/mobile.js";
import { getNumberWithOrdinalWithSuperscript } from "../../hooks/utils.js";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer } from "deck.gl";

const MAX_BOUNDS = [
  [-170, -55], // Southwest coordinates
  [200, 75], // Northeast coordinates
];
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const LONGITUDE_FLIP = 50;

const DEFAULT_MAP_STATE = {
  maxZoom: MAX_ZOOM,
  minZoom: MIN_ZOOM,
  zoom: 0,
  longitude: 0,
  latitude: 0,
};

const useCountriesData = () => {
  const [loadedCountriesData, setLoadedCountriesData] = React.useState(null);

  React.useEffect(() => {
    // Only load shapes once.
    (async () => {
      const res = await axios(`./countries-simplified-20.topojson`);
      const fc = topojsonParse(res.data, "map (2)");
      setLoadedCountriesData(fc);
    })();
  }, []);

  return loadedCountriesData;
};

export default function MapEmbed() {
  let { citiesData } = useCitiesData();
  const [hoveredCity, setHoveredCity] = React.useState(null);
  const [allCitiesData, setAllCitiesData] = React.useState(null);
  const [selectedCountries, setSelectedCountries] = React.useState([]);
  const [unselectedCountries, setUnselectedCountries] = React.useState([]);
  const [hideList, setHideList] = React.useState(true);
  const [minZoom, setMinZoom] = React.useState(MIN_ZOOM);
  const isMobile = useIsMobile();
  const [viewport, setViewport] = React.useState(DEFAULT_MAP_STATE);
  let mapRef = React.useRef(null);
  let tableRef = React.useRef(null);
  let { width, height } = useComponentSize(mapRef);
  const countriesData = useCountriesData();

  React.useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = React.useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = React.useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = React.useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );

  const handleHover = (city, fromMap = false) => {
    setHoveredCity(city);
    if (city && fromMap && !isMobile) {
      const element = document.getElementById(`table-row-${city}`);
      tableRef.current.scrollTo(0, element.offsetTop - 30);
    }
  };

  React.useEffect(() => {
    if (citiesData && countriesData) {
      const data = citiesData
        .map((d) => {
          return {
            ...d,
            city: d.city ? d.city : d.country,
          };
        })
        .sort((a, b) => {
          const byValue = a.rank - b.rank;
          const byName = a.city.localeCompare(b.city);
          return byValue || byName;
        });
        
      setAllCitiesData(data);
      const includeCountries = data.map((d) => {
        return d.country;
      });

      let features = countriesData.features;

      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.NAME)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);

      const unfilteredFeatures = features.filter(
        (feature) => !includeCountries.includes(feature.properties.NAME)
      );
      const notVisibleCountries = {
        type: "FeatureCollection",
        features: unfilteredFeatures,
      };
      setUnselectedCountries(notVisibleCountries);
    }
  }, [citiesData, countriesData]);

  const handleShowHideList = () => {
    setHideList(!hideList);
  };

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };

  return (
    <div className={styles.mapEmbed}>
      <h2 className={styles.embedH2}>Global Residence Program Index</h2>
      <div className={styles.mapEmbedContainer}>
        <div className={styles.leftColumn} data-expanded={!hideList}>
          <h3>
            {isMobile &&
              "Zoom in to select a country on the map to see country scores and ranks, or see the list below"}
            {!isMobile &&
              "Click on the map or the list below to see country scores and ranks"}
          </h3>
          <div className={styles.tableContainer} ref={tableRef}>
            <table className={styles.mapTable}>
              <thead>
                <tr className={styles.mapTableHeader}>
                  <th className={styles.headerRank}>Rank</th>
                  <th className={styles.headerCity}>Country</th>
                  <th className={styles.headerScore}>Total Score</th>
                </tr>
              </thead>
              <tbody>
                {allCitiesData &&
                  allCitiesData.map((d) => {
                    return (
                      <tr
                        id={`table-row-${d.city}`}
                        key={d.city}
                        style={{
                          backgroundColor:
                            hoveredCity === d.city ? "#fff" : null,
                        }}
                        onMouseOver={() => {
                          handleHover(d.city);
                        }}
                        onMouseOut={() => {
                          handleHover(null);
                        }}
                      >
                        <td className={styles.rankColumn}>
                          {getNumberWithOrdinalWithSuperscript(d.rank)}
                        </td>
                        <td className={styles.citynameColumn}>{d.countryDisplay}</td>
                        <td className={styles.scoreColumn}>
                          {d.totalScore.toFixed(0)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {isMobile && (
              <div className={styles.showButton}>
                <button className={styles.button} onClick={handleShowHideList}>
                  {hideList ? "Show All 25 Cities" : "Collapse list"}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.rightColumn} ref={mapRef}>
          <DeckGL
            viewState={viewport}
            controller={true}
            onViewStateChange={handleViewStateChange}
          >
            <GeoJsonLayer
              id="map-unselected-countries"
              data={unselectedCountries ? unselectedCountries : null}
              getLineColor={[255, 255, 255]}
              getFillColor={[233, 233, 233]}
              lineWidthUnits={"pixels"}
              stroked={true}
            />
            <GeoJsonLayer
              id="map-selected-countries"
              data={selectedCountries ? selectedCountries : null}
              getLineColor={[255, 255, 255]}
              getFillColor={[193, 197, 200]}
              lineWidthUnits={"pixels"}
              stroked={true}
            />
          </DeckGL>
          <svg
            className={styles.cityMarkers}
            height={"100%"}
            width={"100%"}
            viewBox={`0 0 ${width} ${height}`}
          >
            {allCitiesData &&
              allCitiesData.map((d) => {
                return (
                  <circle
                    className={styles.circle}
                    key={d.city + "-marker"}
                    cx={getProjectionCoordinates([d.longitude, d.latitude])[0]}
                    cy={getProjectionCoordinates([d.longitude, d.latitude])[1]}
                    r={hoveredCity === d.city ? 10 : 5}
                    fill={"#405363"}
                    stroke={"#fff"}
                    onTouchStart={() => {
                      handleHover(d.city, true);
                    }}
                    onMouseEnter={() => {
                      handleHover(d.city, true);
                    }}
                    onMouseOut={() => {
                      handleHover(null);
                    }}
                  >
                    <title>{d.city}</title>
                  </circle>
                );
              })}
          </svg>
          {allCitiesData &&
            allCitiesData.map((d) => {
              return (
                <React.Fragment key={d.city + "-tooltip"}>
                  {hoveredCity === d.city && (
                    <div
                      className={
                        isMobile ? styles.cityHoverMobile : styles.cityHover
                      }
                      style={{
                        top:
                          getProjectionCoordinates([
                            d.longitude,
                            d.latitude,
                          ])[1] - 10,
                        left:
                          getProjectionCoordinates([
                            d.longitude,
                            d.latitude,
                          ])[0] - 10,
                      }}
                    >
                      <div
                        className={
                          d.longitude > LONGITUDE_FLIP
                            ? styles.cityHoverContainerFlip +
                              " " +
                              styles.cityHoverContainer
                            : styles.cityHoverContainer
                        }
                      >
                        <span className={styles.rankHover}>
                          {getNumberWithOrdinalWithSuperscript(d.rank)}
                        </span>
                        <span className={styles.citynameHover}>{d.countryDisplay}</span>
                        <span className={styles.scoreHover}>
                          {d.totalScore.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          <div className={styles.mapControls}>
            <div
              className={styles.mapZoomButton}
              onClick={() => zoomIncrement(0.5)}
            >
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13 17V30H17V17H30V13H17V0H13V13H0V17H13Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div
              className={styles.mapZoomButton}
              onClick={() => zoomIncrement(-0.5)}
            >
              <svg width="30" height="4" viewBox="0 0 30 4" fill="none">
                <rect width="30" height="4" fill="currentColor" />
              </svg>
            </div>
            <div
              className={styles.mapZoomButton}
              onClick={() => resetZoom()}
              title="Zoom to world"
            >
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path
                  fill="currentColor"
                  d="M12 .387085C5.37097.387085 0 5.75805 0 12.3871c0 6.629 5.37097 12 12 12 6.629 0 12-5.371 12-12C24 5.75805 18.629.387085 12 .387085Zm9.3484 7.354835h-3.9919c-.4355-2.14838-1.1662-3.97742-2.0904-5.27903 2.6613.88065 4.8484 2.80161 6.0823 5.27903Zm-5.0903 4.64518c0 1.108-.0775 2.1387-.2081 3.0968h-8.1c-.13065-.9581-.20806-1.9888-.20806-3.0968 0-1.1081.07741-2.1387.20806-3.09679h8.1c.1306.95809.2081 1.98869.2081 3.09679ZM12 1.93547c1.3016 0 2.971 2.13387 3.779 5.80645H8.22097C9.02903 4.06934 10.6984 1.93547 12 1.93547Zm-3.26613.52742C7.81452 3.75967 7.07903 5.5887 6.64355 7.74192H2.65161C3.88548 5.2645 6.07258 3.34354 8.73387 2.46289ZM1.54839 12.3871c0-1.079.16451-2.1194.46935-3.09679h4.37903c-.1258.99189-.20322 2.02259-.20322 3.09679 0 1.0742.07258 2.1048.20322 3.0968H2.01774c-.30484-.9775-.46935-2.0178-.46935-3.0968Zm1.10322 4.6451h3.99194c.43548 2.1484 1.16613 3.9775 2.09032 5.2791-2.66129-.8807-4.84839-2.8016-6.08226-5.2791ZM12 22.8387c-1.3016 0-2.97097-2.1339-3.77903-5.8065H15.779c-.808 3.6726-2.4774 5.8065-3.779 5.8065Zm3.2661-.5274c.9194-1.2968 1.6549-3.1258 2.0904-5.2791h3.9919c-1.2339 2.4775-3.421 4.3984-6.0823 5.2791Zm2.3371-6.8274c.1258-.992.2033-2.0226.2033-3.0968 0-1.0742-.0726-2.1049-.2033-3.09679h4.3791c.3048.97739.4693 2.01779.4693 3.09679 0 1.079-.1645 2.1193-.4693 3.0968h-4.3791Z"
                ></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
